import Tippy from '@tippyjs/react';
import { Spacing16 } from 'autogen/design-tokens/tokens';
import type { GiftCardInfoBox } from 'autogen/swagger/all';
import {
    GetGiftCardInfoQueryKey,
    useActivatePaymentByGiftCardMutation,
    useDeactivatePaymentByGiftCardMutation,
    useGetGiftCardInfoQuery
} from 'autogen/swagger/all/hooks';
import {
    HideGiftCard,
    PayByGiftCard,
    ShowGiftCard,
    ToolTipText
} from 'autogen/translation-keys/trans-website-checkout-giftcard';
import { ButtonWithIcon } from 'components/Button';
import Icon from 'components/Icon';
import Flex from 'components/Layout/components/Flex';
import { Typography } from 'components/generic/typography';
import { GA4Tracking } from 'modules/tracking/GA4';
import { useTranslation } from 'react-i18next';
import queryClient from 'setup/react-query/queryClient';
import styled from 'styled-components';
import GiftCardForm from './components/GiftCardForm';
import HighlightedText from 'components/generic/HighlightedText';
import { updateBasket } from 'components/basket/shared/basket';

const ArrowUp = styled(Icon)<{ $isOpen: boolean }>`
    transform: ${({ $isOpen }) => ($isOpen ? 'none;' : 'rotate(180deg);')};
`;

export default function PaymentByGiftCard({
    model: initialModel,
    alignment
}: { model: GiftCardInfoBox; alignment: 'start' | 'center' | 'end' }) {
    const { t } = useTranslation();
    const { data: model } = useGetGiftCardInfoQuery({ initialData: initialModel });
    const { giftCardLines, disabledText, payByGiftCard, isDisabled, hasActiveGiftCards, usage } = model || initialModel;
    const onSuccess = () => {
        queryClient.invalidateQueries({ queryKey: [GetGiftCardInfoQueryKey] });
        if (usage === 'Basket') {
            updateBasket();
        }
    };
    const { mutateAsync: activatePaymentByGiftCard } = useActivatePaymentByGiftCardMutation({
        onSuccess
    });
    const { mutateAsync: deactivatePaymentByGiftCard } = useDeactivatePaymentByGiftCardMutation({
        onSuccess
    });

    function handleToggle() {
        if (!payByGiftCard) {
            activatePaymentByGiftCard({});
        } else {
            deactivatePaymentByGiftCard({});
        }

        GA4Tracking({
            eventName: 'toggle_gift_card',
            context: 'summary',
            category: 'ecommerce',
            eventParams: [
                { param_name: 'checkout_step', param_value: 3 },
                { param_name: 'toggle', param_value: !payByGiftCard }
            ]
        });
    }

    function handleTooltipTracking() {
        GA4Tracking({
            eventName: 'read_more',
            context: 'summary',
            category: 'ecommerce',
            eventParams: [
                { param_name: 'checkout_step', param_value: 3 },
                { param_name: 'read_more_type', param_value: 'tooltip' }
            ]
        });
    }

    if (isDisabled) {
        return (
            <HighlightedText withBorder={false}>
                <Typography.Body size="M" as="div">
                    {disabledText}
                </Typography.Body>
            </HighlightedText>
        );
    }

    return (
        <Flex.Column alignItems={alignment} gap={Spacing16}>
            {!hasActiveGiftCards && (
                <ButtonWithIcon
                    type="button"
                    variant="primaryText"
                    icon={<ArrowUp type="ChevronUp" size={16} $isOpen={payByGiftCard} />}
                    iconProperties={{ position: 'right' }}
                    onClick={handleToggle}
                >
                    {!payByGiftCard ? t(ShowGiftCard) : t(HideGiftCard)}
                </ButtonWithIcon>
            )}

            {payByGiftCard && (
                <>
                    <GiftCardForm model={giftCardLines} />
                    <Tippy theme="plain-tooltip" trigger="click" content={t(ToolTipText)}>
                        <ButtonWithIcon
                            variant="primaryText"
                            icon={<Icon type="Information" size={16} />}
                            iconProperties={{ position: 'right' }}
                            type="button"
                            onClick={handleTooltipTracking}
                        >
                            {t(PayByGiftCard)}
                        </ButtonWithIcon>
                    </Tippy>
                </>
            )}
        </Flex.Column>
    );
}
